import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import useThunkDispatch from '../../common/useThunkDispatch'
import Splash from '../../components/Splash'
import Modal from '../../components/Modal'
import LocationInventoryHeader from './LocationInventoryHeader'
import LocationInventoryRow from './LocationInventoryRow'
import {
  fetchLocationWithItems,
  searchInventoryProducts,
} from '../locationsActions'
import Search from '../../components/Search'
import { money } from '../../common/display'
import LocationInventoryProductRow from './LocationInventoryProductRow'
import {
  deleteLocationItem,
  createLocationItem,
  updateLocationItem,
} from '../locationsActions'
import { IProduct } from '../../types/products'
import withAccountAndBranch from '../../common/withAccountAndBranch'
import { getShouldRenderTruckInventoryFeature } from '../../common/getShouldRenderTruckInventoryFeature'
import { getShouldRenderBinInventoryFeature } from '../../common/getShouldRenderBinInventoryFeature'
import { IAccount, IBranch, IUser } from '../../types/user'

interface IProps {
  branch: IBranch
  account?: IAccount
  user: IUser
}

const LocationInventory: FC<IProps> = ({ branch, account, user }) => {
  const {
    locationId: locationIdParam,
    accountId: accountIdParam,
    branchId: branchIdParam,
  } = useParams()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const dispatch = useThunkDispatch()
  const [loading, setLoading] = useState(true)
  const [locationCode, setLocationCode] = useState('')
  const [branchName, setBranchName] = useState<string | undefined>(undefined)
  const [items, setItems] = useState<any[]>([])
  const [totalValue, setTotalValue] = useState<number>(0)
  const [term, setTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const [foundProducts, setFoundProducts] = useState<any[] | null>(null)
  const listRef = useRef<FixedSizeList>(null)
  const multiCatalogEnabled =
    (account?.erpCatalogs || user?.account?.erpCatalogs || []).length > 1
  const [locationId, setLocationId] = useState<number | null>(null)
  const [hasCycleCounts, setHasCycleCounts] = useState<boolean>(false)
  const [locationType, setLocationType] = useState<string | undefined>(
    undefined,
  )
  const accountId = account?.id ?? user?.account?.id
  const branchIdProp = branch?.id

  const filteredItems = useMemo(() => {
    const isTruckBeforeFirstCount = locationType === 'truck' && !hasCycleCounts
    const visibleItems = items.filter(
      (it) => Number(it?.inventory) > 0 || isTruckBeforeFirstCount,
    )
    if (!hasCycleCounts) return visibleItems
    const t = debouncedTerm.trim().toLowerCase()
    if (!t) return visibleItems
    return visibleItems.filter((it) => {
      const name = it?.product?.name?.toLowerCase() || ''
      const code = it?.product?.code?.toLowerCase() || ''
      const idStr = String(it?.product?.id || '')
      return (
        name.includes(t) || code.includes(t) || idStr.includes(debouncedTerm)
      )
    })
  }, [hasCycleCounts, debouncedTerm, items, locationType])

  const isLocationEmpty =
    hasCycleCounts && !foundProducts && filteredItems.length === 0

  const emptyMessage = useMemo(() => {
    if (locationType === 'bin') {
      return getShouldRenderBinInventoryFeature(
        account?.binInventoryFeature ?? user?.account?.binInventoryFeature,
        branch?.binInventoryFeature,
      )
        ? 'Add products with a bin move'
        : undefined
    }
    return getShouldRenderTruckInventoryFeature(
      account?.truckInventoryFeature ?? user?.account?.truckInventoryFeature,
      branch?.truckInventoryFeature,
    )
      ? 'Add products with a checkout or a move'
      : undefined
  }, [locationType, account, branch, user])

  const emptyTitle =
    locationType === 'bin' ? 'This bin is empty' : 'This truck is empty'

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const id = parseInt(locationIdParam ?? '', 10)
        const location = await dispatch(
          fetchLocationWithItems(id, branchIdProp),
        )
        setLocationCode(location.code)
        setBranchName(location.branches?.[0]?.name)
        setItems(location.locationItems || [])
        setTotalValue(location.totalValue || 0)
        setLocationId(location.id)
        setHasCycleCounts(!!location.hasCycleCounts)
        setLocationType(location.locationType)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [locationIdParam, branchIdProp])

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedTerm(term), 300)
    return () => clearTimeout(handler)
  }, [term])

  useEffect(() => {
    const performSearch = async () => {
      if (hasCycleCounts) {
        setFoundProducts(null)
        if (listRef.current) listRef.current.scrollTo(0)
        return
      }

      if (!debouncedTerm) {
        setFoundProducts(null)
        return
      }

      if (!accountId || !branchIdProp) return
      setSearching(true)
      const results = await dispatch(
        searchInventoryProducts(branchIdProp, debouncedTerm, accountId),
      )
      setFoundProducts(results)
      if (listRef.current) listRef.current.scrollTo(0)
      setSearching(false)
    }
    performSearch()
  }, [debouncedTerm, accountId, branchIdProp, hasCycleCounts])

  useEffect(() => {
    const total = items.reduce((sum, item) => {
      const inventory = Number(item?.inventory || 0)
      const price = Number(item?.price ?? item?.product?.price ?? 0)
      return sum + inventory * price
    }, 0)
    setTotalValue(total)
  }, [items])

  const handleDelete = useCallback(
    async (item: any) => {
      if (!item?.id) return
      const { success, errors } = await dispatch(deleteLocationItem(item.id))
      if (success) {
        setItems((prev) => prev.filter((i) => i.id !== item.id))
      } else if (errors) {
        alert(errors)
      }
    },
    [dispatch],
  )

  const handleAdd = useCallback(
    async (product: IProduct) => {
      if (!locationId) return
      const unit = product.unit || 'EACH'
      const price = product.price || 0
      const { result, errors } = await dispatch(
        createLocationItem({
          locationId,
          productId: product.id,
          inventory: 0,
          price,
          unit,
        }),
      )
      if (result) {
        setItems((prev) => [{ ...result, product }, ...prev])
      } else if (errors) {
        alert(errors)
      }
    },
    [dispatch, locationId],
  )

  const handleThresholdUpdate = useCallback(
    async (itemId: number, threshold: number | null) => {
      const { result, errors } = await dispatch(
        updateLocationItem(itemId, { threshold }),
      )
      if (result) {
        setItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, threshold: result.threshold }
              : item,
          ),
        )
      } else if (errors) {
        alert(errors)
      }
    },
    [dispatch],
  )

  return (
    <Modal closeable={false} containerClassName="">
      <div className="order-modal">
        <LocationInventoryHeader
          branchName={branchName}
          locationCode={locationCode}
          locationType={locationType}
          onClose={() => {
            const state = routerLocation.state
            if (accountIdParam && branchIdParam) {
              navigate(
                `/clients/${accountIdParam}/branches/${branchIdParam}/locations`,
                { state },
              )
            } else {
              navigate('/locations', { state })
            }
          }}
        />
        <div className="form">
          {!isLocationEmpty && (
            <div className="order-modal_search">
              <div className="order-modal_field">
                <Search
                  placeholder={
                    hasCycleCounts
                      ? 'Filter location items by name or ID'
                      : 'Search item name or ID to add'
                  }
                  value={term}
                  onChange={(e) =>
                    setTerm(e.target.value ? e.target.value.trim() : '')
                  }
                />
              </div>
              <div className="order-modal_total">
                Total
                <br />
                <strong>{money(totalValue.toFixed(2))}</strong>
              </div>
            </div>
          )}
          <div className="order-modal_list">
            {isLocationEmpty ? (
              <div
                className="replenishment-empty"
                style={{ minHeight: '500px' }}
              >
                <div className="replenishment-empty_title">{emptyTitle}</div>
                {emptyMessage && (
                  <div className="replenishment-empty_message">
                    {emptyMessage}
                  </div>
                )}
              </div>
            ) : (
              <AutoSizer
                className="table"
                style={{ height: 'calc(100vh - 265px)' }}
              >
                {(size) => (
                  <FixedSizeList
                    ref={listRef}
                    className="wonder-scroll"
                    {...size}
                    itemData={{
                      items: filteredItems,
                      allItems: items,
                      products: foundProducts || undefined,
                      onAdd: foundProducts ? handleAdd : undefined,
                      onDelete:
                        foundProducts || hasCycleCounts
                          ? undefined
                          : handleDelete,
                      onThresholdUpdate: handleThresholdUpdate,
                      locationType,
                      multiCatalogEnabled,
                      searching,
                      loading: searching,
                    }}
                    itemCount={
                      foundProducts
                        ? foundProducts.length
                        : filteredItems.length
                    }
                    itemSize={100 + 2}
                    itemKey={(index) =>
                      foundProducts
                        ? foundProducts[index].id
                        : filteredItems[index].id
                    }
                  >
                    {foundProducts
                      ? LocationInventoryProductRow
                      : LocationInventoryRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
          </div>
        </div>
        <Splash isLoading={loading} bgColor="#F2F6FA" />
      </div>
    </Modal>
  )
}

export default withAccountAndBranch(LocationInventory)
